import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils/cn";
import { thematicClasses } from "@/lib/thematic";

export interface SideNavGoal {
  id: string;
  label: string;
  /** Right-aligned tally, e.g. "3/8". */
  count: string;
  /** Draw attention: something is missing under this objective. */
  warn?: boolean;
}

export interface SideNavIssue {
  id: string;
  name: string;
  /** Backend thematic key, for the colour. */
  cssClass: string;
  count: string;
  goals: SideNavGoal[];
}

/**
 * The workspace steps that list every objective get long — a plan can carry 46
 * of them across 16 issues — so they share this index: each issue with its
 * objectives and tallies, and a jump to any of them.
 *
 * Sticky, and capped to the viewport with its own scrollbar: a nav taller than
 * the screen would otherwise pin in place with its bottom permanently out of
 * reach, since page scroll doesn't move a stuck element.
 */
export function StepSideNav({
  issues,
  onJump,
}: {
  issues: SideNavIssue[];
  onJump: (goalId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t("actionPlans.nav.stepIndex")}
      className={cn(
        "overflow-hidden rounded-lg border bg-card",
        // An index in front of the content is just something to scroll past on
        // a phone — 62 rows for a 16-issue plan — so it follows the list there
        // and only becomes a sticky sidebar once there's a column for it.
        "order-last lg:order-first",
        "lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto",
      )}
    >
      {issues.map((issue) => {
        const tc = thematicClasses(issue.cssClass);
        return (
          <div key={issue.id} className="border-b last:border-b-0">
            <div
              className={cn(
                "flex items-center gap-2 px-2.5 py-1.5 text-[12px] font-bold",
                tc.softBg,
                tc.ink,
              )}
            >
              <span className="min-w-0 flex-1 truncate">{issue.name}</span>
              <span className="shrink-0 text-[11px] tabular-nums opacity-85">
                {issue.count}
              </span>
            </div>
            {issue.goals.map((goal) => (
              <button
                key={goal.id}
                type="button"
                onClick={() => onJump(goal.id)}
                className="flex w-full items-center gap-2 border-t border-border/60 py-1.5 pl-5 pr-2.5 text-left text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {goal.warn && (
                  <span
                    aria-hidden
                    className="size-1.5 shrink-0 rounded-full bg-warning"
                  />
                )}
                <span className="min-w-0 flex-1 truncate">{goal.label}</span>
                <span
                  className={cn(
                    "shrink-0 text-[11px] tabular-nums",
                    goal.warn && "font-bold text-warning",
                  )}
                >
                  {goal.count}
                </span>
              </button>
            ))}
          </div>
        );
      })}
    </nav>
  );
}
